import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import styled from 'styled-components';

import { SEARCH_SOURCE } from '@rover/constants/src/search/searchPage.constants';
import { SERVICE_TYPE_CHOICES } from '@rover/constants/src/translations/service.constants';
import CarouselRightSmall from '@rover/icons/svg/carousel-right-small.svg';
import events from '@rover/react-lib/src/pages/search/SearchPage/analytics';
import { getWizardTitle } from '@rover/react-lib/src/pages/search/SearchPage/utilities';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import type { DataLayerEvent } from '@rover/types/src/DataLayerEvent';
import Enum from '@rover/types/src/Enum';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import type { ServiceType } from '@rover/types/src/ServiceType';

import { SearchInterstitialSearchCta } from '../../analyticsEvents';
import { PaginatedWizardFiltersCollection } from '../../Filters/Collections';

import { SearchInterstitialDesktopNext } from './analyticsEvent';
import StyledDialogModal from './StyledDialogModal';

export class WizardPage extends Enum<string> {
  static ONE = new WizardPage('one');

  static TWO = new WizardPage('two');
}

type Props = {
  fireAnalyticsEvent: (
    payload: DataLayerEvent | ((filters: Partial<SearchFilters>) => DataLayerEvent)
  ) => void;
  getServiceTypeBySlug: (arg0: string) => ServiceType | null | undefined;
  hasPets: boolean;
  isCatOnly: boolean;
  isOpen: boolean;
  disablePortal?: boolean;
  onClose: () => void;
  searchFilters: SearchFilters;
  updateFiltersAndFireSearchOnChange: (arg0: Record<string, unknown>) => void;
  setShowStaticMap: (arg0: boolean) => void;
  setShowDynamicMap: (arg0: boolean) => void;
  className?: string;
};

const NextArrow = styled(CarouselRightSmall)`
  fill: white;
  height: 24px;
  left: ${({ theme }) => theme.space['2x']};
  position: relative;
  top: -1px;
  vertical-align: middle;
  width: 24px;
`;

const NextButtonContent = (): JSX.Element => (
  <>
    <Trans>Next</Trans>
    <NextArrow />
  </>
);

export const PaginatedWizardModal: React.FC<Props> = ({
  fireAnalyticsEvent,
  getServiceTypeBySlug,
  hasPets,
  isCatOnly,
  isOpen,
  disablePortal,
  onClose,
  searchFilters,
  updateFiltersAndFireSearchOnChange,
  setShowStaticMap,
  setShowDynamicMap,
  className,
}) => {
  const { t, i18n } = useLingui();
  const [visiblePage, setVisiblePage] = useState<WizardPage>(WizardPage.ONE);
  const [didUserCloseModal, setDidUserCloseModal] = useState(false);
  const modalRef = useRef<HTMLDivElement | null>(null);

  // Handle showing/hiding map when modal closes
  useEffect(() => {
    if (didUserCloseModal) {
      setShowDynamicMap(true);
      setShowStaticMap(false);
    }
  }, [didUserCloseModal, setShowDynamicMap, setShowStaticMap]);

  const closeModal = useCallback(() => {
    fireAnalyticsEvent(events.SearchOwnerPreferencesCloseEvent);
    setDidUserCloseModal(true);
    onClose();
  }, [fireAnalyticsEvent, onClose]);

  const showNextPage = useCallback(() => {
    emitAnalyticsEvent(new SearchInterstitialDesktopNext());
    setVisiblePage(WizardPage.TWO);
    if (modalRef.current) {
      modalRef.current.scrollTop = 0;
    }
  }, []);

  const handleSubmit = useCallback(() => {
    fireAnalyticsEvent(events.SearchOwnerPreferencesCloseEvent);
    emitAnalyticsEvent(new SearchInterstitialSearchCta());
    onClose();
    updateFiltersAndFireSearchOnChange({
      ...searchFilters,
      changeSource: SEARCH_SOURCE.SEARCH_MODAL,
    });
  }, [fireAnalyticsEvent, onClose, updateFiltersAndFireSearchOnChange, searchFilters]);

  const hasNextPage = (): boolean => {
    const serviceType = getServiceTypeBySlug(searchFilters.serviceType);
    const isOnDemand = serviceType && !serviceType.browsable;
    return !(hasPets || isCatOnly || isOnDemand) && visiblePage === WizardPage.ONE;
  };

  const isGrooming = searchFilters.serviceType === SERVICE_TYPE_CHOICES.grooming;
  const searchText = isGrooming ? t`Request Grooming` : t`Search Now`;
  const onClickSubmit = hasNextPage() ? showNextPage : handleSubmit;

  return (
    <StyledDialogModal
      disablePortal={disablePortal}
      headerText={i18n._(getWizardTitle(isCatOnly))}
      isOpen={isOpen}
      onClickCancel={null}
      onRequestClose={closeModal}
      overlayRef={(node) => (modalRef.current = node)}
      data={{
        'qa-id': SEARCH_SOURCE.SEARCH_MODAL,
        testId: SEARCH_SOURCE.SEARCH_MODAL,
      }}
      useSearchPerfHack
      className={className}
      onClickSubmit={onClickSubmit}
      submitButtonText={hasNextPage() ? <NextButtonContent /> : searchText}
    >
      <PaginatedWizardFiltersCollection
        locationInputId="location-input-wizard-modal"
        visiblePage={visiblePage}
      />
    </StyledDialogModal>
  );
};

export default PaginatedWizardModal;
